import {
    Box,
    Button,
    Flex,
    FormControl,
    Text,
    Textarea,
    VStack,
    Checkbox,
    FormErrorMessage
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useCallback } from "react";
import {
    Cookie,
    AutoSave
} from '../../../utils/cookie-utils';
import InlineErrorInput from "../../InlineErrorInput/InlineErrorInput";

const CONSENT_FIELDS = ["regulationsConsent", "dataTransferConsent", "newsletterConsent", "organizerNewsletterConsent", "commercialInfoConsent", "profilingConsent"];

const UserDataStep = ({ data, update, next, t }) => {
    const UserDataSchema = useMemo(() => Yup.object().shape({
        isCompany: Yup.boolean(),
        firstName: Yup.string().when("isCompany", {
            is: false,
            then: () => Yup.string().required(t("order.form.validation.name") || "Imię jest wymagane"),
        }),
        lastName: Yup.string().when("isCompany", {
            is: false,
            then: () => Yup.string().required(t("order.form.validation.lastName") || "Nazwisko jest wymagane"),
        }),
        email: Yup.string().email(t("order.form.validation.email_invalid") || "Nieprawidłowy e-mail").required(t("order.form.validation.email") || "E-mail jest wymagany"),
        phone: Yup.string(),
        info: Yup.string(),
        buyerCompanyName: Yup.string().when("isCompany", {
            is: true,
            then: () => Yup.string().required(t("order.form.validation.companyName") || "Nazwa firmy jest wymagana"),
        }),
        buyerNIP: Yup.string().when("isCompany", {
            is: true,
            then: () => Yup.string().required(t("order.form.validation.nip") || "NIP jest wymagany"),
        }),
        buyerAddress: Yup.string().when("isCompany", {
            is: true,
            then: () => Yup.string().required(t("order.form.validation.address") || "Adres jest wymagany"),
        }),
        buyerPostalCode: Yup.string().when("isCompany", {
            is: true,
            then: () => Yup.string().required(t("order.form.validation.postalCode") || "Kod pocztowy jest wymagany"),
        }),
        buyerCity: Yup.string().when("isCompany", {
            is: true,
            then: () => Yup.string().required(t("order.form.validation.city") || "Miejscowość jest wymagana"),
        }),
        regulationsConsent: Yup.boolean().oneOf([true], t("order.form.validation.consent") || "Zgoda jest wymagana"),
        dataTransferConsent: Yup.boolean().oneOf([true], t("order.form.validation.consent") || "Zgoda jest wymagana"),
        newsletterConsent: Yup.boolean(),
        organizerNewsletterConsent: Yup.boolean(),
        commercialInfoConsent: Yup.boolean(),
        profilingConsent: Yup.boolean(),
    }), [t]);

    const eventId = data?.eventId ?? data?.id ?? "global";
    const COOKIE_KEY = `checkout:${eventId}:user`;

    const cookieVals = Cookie.get(COOKIE_KEY);
    const initialValues = useMemo(() => ({
        isCompany: cookieVals?.isCompany ?? data.isCompany ?? false,
        firstName: cookieVals?.firstName ?? data.firstName ?? "",
        lastName: cookieVals?.lastName ?? data.lastName ?? "",
        email: cookieVals?.email ?? data.email ?? "",
        phone: cookieVals?.phone ?? data.phone ?? "",
        info: cookieVals?.info ?? data.info ?? "",
        buyerCompanyName: cookieVals?.buyerCompanyName ?? data.buyerCompanyName ?? "",
        buyerNIP: cookieVals?.buyerNIP ?? data.buyerNIP ?? "",
        buyerAddress: cookieVals?.buyerAddress ?? data.buyerAddress ?? "",
        buyerPostalCode: cookieVals?.buyerPostalCode ?? data.buyerPostalCode ?? "",
        buyerCity: cookieVals?.buyerCity ?? data.buyerCity ?? "",
        regulationsConsent: cookieVals?.regulationsConsent ?? data.regulationsConsent ?? false,
        dataTransferConsent: cookieVals?.dataTransferConsent ?? data.dataTransferConsent ?? false,
        newsletterConsent: cookieVals?.newsletterConsent ?? data.newsletterConsent ?? false,
        organizerNewsletterConsent: cookieVals?.organizerNewsletterConsent ?? data.organizerNewsletterConsent ?? false,
        commercialInfoConsent: cookieVals?.commercialInfoConsent ?? data.commercialInfoConsent ?? false,
        profilingConsent: cookieVals?.profilingConsent ?? data.profilingConsent ?? false,
    }), [cookieVals, data]);

    useEffect(() => {
        if (cookieVals) update(initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAutoSave = useCallback((vals) => {
        Cookie.set(COOKIE_KEY, vals, 7);
        update(vals);
    }, [COOKIE_KEY, update]);

    const isAllConsentSelected = (values) =>
        CONSENT_FIELDS.every((f) => !!values[f]);

    const handleSelectAll = (values, setFieldValue) => {
        const allSelected = isAllConsentSelected(values);
        CONSENT_FIELDS.forEach((f) => setFieldValue(f, !allSelected));
    };

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={UserDataSchema}
            onSubmit={(values) => {
                Cookie.set(COOKIE_KEY, values, 7);
                update(values);
                next();
            }}
        >
            {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                    <AutoSave values={values} onSave={handleAutoSave} />
                    <VStack spacing={6} align="stretch">
                        <Box>
                            <Text color="#717171" fontSize="14px" fontFamily="body">
                                {t('order.step.user_data.description')}
                            </Text>
                        </Box>

                        <Flex w="full" borderBottom="1px solid #ECE4E4" mb={2}>
                            <Flex
                                flex="1"
                                py={3}
                                justify="center"
                                align="center"
                                cursor="pointer"
                                borderBottom={!values.isCompany ? "2px solid #7A1F35" : "none"}
                                onClick={() => setFieldValue("isCompany", false)}
                                gap={2}
                            >
                                <Box 
                                    w="16px" h="16px" borderRadius={'12px'} border="1px solid" 
                                    borderColor={!values.isCompany ? "#7A1F35" : "#DED3D4"}
                                    bg={!values.isCompany ? "#7A1F35" : "white"}
                                    display="flex" alignItems="center" justifyContent="center"
                                >
                                    {!values.isCompany && (
                                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </Box>
                                <Text fontWeight="600" color={!values.isCompany ? "#1E1517" : "#6E6064"} fontSize="14px" fontFamily="body">
                                    {t('order.form.type.private')}
                                </Text>
                            </Flex>
                            <Flex
                                flex="1"
                                py={3}
                                justify="center"
                                align="center"
                                cursor="pointer"
                                borderBottom={values.isCompany ? "2px solid #7A1F35" : "none"}
                                onClick={() => setFieldValue("isCompany", true)}
                                gap={2}
                            >
                                <Box 
                                    w="16px" h="16px" borderRadius={'12px'} border="1px solid" 
                                    borderColor={values.isCompany ? "#7A1F35" : "#DED3D4"}
                                    bg={values.isCompany ? "#7A1F35" : "white"}
                                    display="flex" alignItems="center" justifyContent="center"
                                >
                                    {values.isCompany && (
                                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </Box>
                                <Text fontWeight="600" color={values.isCompany ? "#1E1517" : "#6E6064"} fontSize="14px" fontFamily="body">
                                    {t('order.form.type.company')}
                                </Text>
                            </Flex>
                        </Flex>

                        {!values.isCompany ? (
                            <>
                                <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="firstName"
                                            label={t('order.form.name')}
                                            isRequired
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="lastName"
                                            label={t('order.form.lastName')}
                                            isRequired
                                        />
                                    </Box>
                                </Flex>
                                <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="email"
                                            type="email"
                                            label={t('order.form.email')}
                                            isRequired
                                            autoComplete="email"
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="phone"
                                            type="tel"
                                            label={t('order.form.phone')}
                                            autoComplete="tel"
                                        />
                                    </Box>
                                </Flex>
                                <InlineErrorInput
                                    name="info"
                                    label={t('order.form.information')}
                                    as={Textarea}
                                    rows={4}
                                    h={'4rem'}
                                />
                            </>
                        ) : (
                            <>
                                <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="buyerCompanyName"
                                            label={t('order.form.companyName')}
                                            isRequired
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="buyerNIP"
                                            label={t('order.form.nip')}
                                            isRequired
                                        />
                                    </Box>
                                </Flex>
                                <InlineErrorInput
                                    name="buyerAddress"
                                    label={t('order.form.address')}
                                    isRequired
                                />
                                <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="buyerPostalCode"
                                            label={t('order.form.postalCode')}
                                            isRequired
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="buyerCity"
                                            label={t('order.form.city')}
                                            isRequired
                                        />
                                    </Box>
                                </Flex>
                                <Flex gap={4} direction={{ base: "column", md: "row" }}>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="email"
                                            type="email"
                                            label={t('order.form.email')}
                                            isRequired
                                            autoComplete="email"
                                        />
                                    </Box>
                                    <Box flex="1">
                                        <InlineErrorInput
                                            name="phone"
                                            type="tel"
                                            label={t('order.form.phone')}
                                            autoComplete="tel"
                                        />
                                    </Box>
                                </Flex>
                            </>
                        )}

                        <Flex flexDirection={"column"} gap="12px">
                            <Text fontWeight="600" fontSize="16px">
                                {t('order.form.consent.messaage')}
                            </Text>

                            {/* Zaznacz wszystkie */}
                            <Checkbox
                                isChecked={isAllConsentSelected(values)}
                                onChange={() => handleSelectAll(values, setFieldValue)}
                                fontWeight="500"
                                alignItems="flex-start"
                                colorScheme="teal"
                            >
                                <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.selectAll')}</Text>
                            </Checkbox>

                            <Box h="1px" bg="#ECE4E4" my="4px" />

                            {/* Wymagane: Regulamin */}
                            <Field name="regulationsConsent">
                                {({ field, form }) => (
                                    <FormControl isInvalid={form.errors.regulationsConsent && form.touched.regulationsConsent}>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.regulationsConsent')}</Text>
                                        </Checkbox>
                                        <FormErrorMessage>{form.errors.regulationsConsent}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>

                            {/* Wymagane: Przekazanie danych Organizatorowi */}
                            <Field name="dataTransferConsent">
                                {({ field, form }) => (
                                    <FormControl isInvalid={form.errors.dataTransferConsent && form.touched.dataTransferConsent}>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.dataTransferConsent')}</Text>
                                        </Checkbox>
                                        <FormErrorMessage>{form.errors.dataTransferConsent}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>

                            <Box h="1px" bg="#ECE4E4" my="4px" />

                            {/* Niewymagane: Newsletter */}
                            <Field name="newsletterConsent">
                                {({ field }) => (
                                    <FormControl>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.newsletterConsent')}</Text>
                                        </Checkbox>
                                    </FormControl>
                                )}
                            </Field>

                            {/* Niewymagane: Newsletter Organizatora */}
                            <Field name="organizerNewsletterConsent">
                                {({ field }) => (
                                    <FormControl>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.organizerNewsletterConsent')}</Text>
                                        </Checkbox>
                                    </FormControl>
                                )}
                            </Field>

                            {/* Niewymagane: Informacje handlowe */}
                            <Field name="commercialInfoConsent">
                                {({ field }) => (
                                    <FormControl>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.commercialInfoConsent')}</Text>
                                        </Checkbox>
                                    </FormControl>
                                )}
                            </Field>

                            {/* Niewymagane: Profilowanie */}
                            <Field name="profilingConsent">
                                {({ field }) => (
                                    <FormControl>
                                        <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                            <Text fontSize="14px" mt="-1" color="gray.700">{t('order.form.consent.profilingConsent')}</Text>
                                        </Checkbox>
                                    </FormControl>
                                )}
                            </Field>
                        </Flex>

                        <Button
                            w={"full"}
                            type="submit"
                            bgColor={'#7A1F35'}
                            color={'#FFFFFF'}
                            borderRadius={'12px'}
                            fontFamily="body"
                            fontWeight="400"
                            fontSize="18px"
                            isLoading={isSubmitting}
                            alignSelf="flex-end"
                            py="30px"
                            gap="12px"
                        >
                            {t('order.form.move_on')}
                            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 5L21.5 12.5M21.5 12.5L14 20M21.5 12.5H3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Button>
                    </VStack>
                </Form>
            )}
        </Formik>
    );
}

export default UserDataStep;
